import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs/Subject';
import { Moment } from 'moment';
import { Observable } from 'rxjs';
import { AccountServiceProvider } from '@services/user/account.service';
import { GraphsDataServiceProvider } from '@web-components/dashboard/graphs-block/graphs.data.service';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { GraphsService } from '@web-services/graph/graphs.service';
import {
	GraphsFilterObject,
	GraphsTotalAmounts,
	JobType,
	RevenueComparisonRangesObject
} from '@models/models';
import { MessageService } from '@services/helpers/message-service/message.service';

import moment = require('moment');

@Component({
	selector: 'graphs-block',
	template: require('./graphs-block.html'),
	styles: [require('./graphs-block.scss')]
})
export class GraphsBlockComponent implements OnInit, OnDestroy {
	@Input() jobTypes: JobType[];

	companyId: number;
	activeTab: string;
	currency: string;
	fromDate: string;
	toDate: string;
	graphTotal: GraphsTotalAmounts = {
		lead: null,
		shoot: null,
		payments: null,
		paymentsDue: null
	};
	leadsData;
	shootsData;
	paymentsData;
	paymentsDueData;
	revenueData = {
		lastYear: null,
		thisYear: null,
		nextYear: null
	};
	minDate: Moment;
	maxDate: Moment;
	unsubscribe: Subject<void> = new Subject<void>();
	private dateRangePreset: string;
	private selectedJobType: number;

	private selectedFilters: GraphsFilterObject = {
		fromDate: null,
		toDate: null,
		jobTypeId: null
	};
	private selectedRange;

	constructor(
		private http: HttpClient,
		private graphsDataService: GraphsDataServiceProvider,
		private timeService: TimeServiceProvider,
		private graphsService: GraphsService,
		private accountService: AccountServiceProvider,
		private messageService: MessageService
	) {}

	ngOnInit(): void {
		this.configureCommunicationAdapter();
		this.setExtremeDates();
		this.getCurrencySymbol();
		this.getCompanyId();
		this.applyChosenJobType();
		this.applyChosenDateRange();
		this.applyFiltersToGraphsOn(['leads', 'shoots', 'payments']);
	}

	ngOnDestroy(): void {
		this.graphsService.clear();
		this.unsubscribe.complete();
		this.unsubscribe.next();
	}

	// GRAPHS DATA MANIPULATION

	applyChosenJobType(): void {
		if (this.selectedJobType === -1) {
			this.selectedFilters.jobTypeId = null;
		} else {
			this.selectedFilters.jobTypeId = this.selectedJobType;
		}
	}

	applyChosenDateRange(): void {
		this.selectedRange = {
			startDate: new Date(this.fromDate).toISOString(),
			endDate: new Date(this.toDate).toISOString()
		};

		this.selectedFilters.fromDate = this.selectedRange.startDate.slice(0, 10);
		this.selectedFilters.toDate = this.selectedRange.endDate.slice(0, 10);
	}

	// VIEW EVENTS HANDLERS

	onChangeActiveTab(newActiveTab: string): void {
		this.activeTab = newActiveTab;
		localStorage.setItem(`dashboardActiveTab${this.companyId}`, newActiveTab);

		this.paintGraphsOnTab(this.activeTab);
	}

	onJobTypeChange(newJobType: string): void {
		this.selectedJobType = parseInt(newJobType);
		localStorage.setItem(`dashboardJobType${this.companyId}`, newJobType);

		this.applyChosenJobType();

		this.applyFiltersToGraphsOn(['leads', 'shoots', 'payments']);
	}

	onDateRangePresetToggle(newRangePreset: string): void {
		if (newRangePreset !== this.dateRangePreset) {
			this.dateRangePreset = newRangePreset;

			localStorage.setItem(
				`presetDashboardDateRange${this.companyId}`,
				this.dateRangePreset
			);
			localStorage.removeItem(`dashboardFromDate${this.companyId}`);
			localStorage.removeItem(`dashboardToDate${this.companyId}`);

			this.setDateRangePreset(this.dateRangePreset);
			this.applyChosenDateRange();
			this.applyFiltersToGraphsOn(['leads', 'shoots', 'payments']);
		}
	}

	setCustomDateRange(newDateRange: {
		startDate: Moment;
		endDate: Moment;
	}): void {
		if (moment.isMoment(newDateRange.startDate)) {
			this.fromDate = this.timeService.getDateString(
				newDateRange.startDate.toDate()
			);
			this.toDate = this.timeService.getDateString(
				newDateRange.endDate.toDate()
			);

			this.dateRangePreset = null;
			localStorage.removeItem(`presetDashboardDateRange${this.companyId}`);
			localStorage.setItem(`dashboardFromDate${this.companyId}`, this.fromDate);
			localStorage.setItem(`dashboardToDate${this.companyId}`, this.toDate);

			this.applyChosenDateRange();
			this.applyFiltersToGraphsOn(['leads', 'shoots', 'payments']);
		}
	}

	// PRESET DATE RANGES

	setDateRangePreset(dateRangePreset: string): void {
		switch (dateRangePreset) {
			case '7': {
				this.setRangeToLastDays(7);
				break;
			}

			case '30': {
				this.setRangeToLastDays(30);
				break;
			}

			case 'm': {
				this.setMonthToDateRange();
				break;
			}

			case 'y': {
				this.setYearToDateRange();
				break;
			}

			default: {
				break;
			}
		}
	}

	setRangeToLastDays(daysBefore: number): void {
		const today = new Date();
		const past = new Date();

		const difference = today.getDate() - daysBefore;
		past.setDate(difference);

		this.fromDate = this.timeService.getDateString(past);
		this.toDate = this.timeService.getDateString(today);
	}

	setMonthToDateRange(): void {
		const today = new Date();
		const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

		this.fromDate = this.timeService.getDateString(monthStart);
		this.toDate = this.timeService.getDateString(today);
	}

	setYearToDateRange(): void {
		const today = new Date();
		const yearStart = new Date(today.getFullYear(), 0, 1);

		this.fromDate = this.timeService.getDateString(yearStart);
		this.toDate = this.timeService.getDateString(today);
	}

	/**
	 * @method applyFiltersToGraphsOn
	 * @desc gets an array of tabs, calculates dynamic number of API calls
	 * and success calbacks, then runs them. When all is done, it calls a
	 * method to paint graph on active tab
	 * @param {string[]} tabs An array of tabs we need fetch new data for
	 * */
	applyFiltersToGraphsOn(tabs: string[]): void {
		const [graphsInfoObservables, successCallbacks]: [
			Observable<any>[],
			Function[]
		] = this.getGraphsObservablesAndSuccessCallbacks(tabs);

		Observable.zip(...graphsInfoObservables)
			.finally((): void => {
				this.paintGraphsOnTab(this.activeTab);
			})
			.subscribe(
				(data): void => {
					successCallbacks.forEach((callback, index) => {
						callback(data[index]);
					});
				},
				(error): void => {
					this.messageService.showDefaultErrorMessage(error);
				}
			);
	}

	// SUCCESS CALLBACKS

	leadsGraphInfoSuccessCallback(leadData): void {
		this.leadsData = JSON.parse(JSON.stringify(leadData.data));
		this.graphTotal.lead = JSON.parse(JSON.stringify(leadData.total));
	}

	shootsGraphInfoSuccessCallback(shootData): void {
		this.shootsData = JSON.parse(JSON.stringify(shootData.data));
		this.graphTotal.shoot = JSON.parse(JSON.stringify(shootData.total));
	}

	paymentsGraphInfoSuccessCallback(paymentData): void {
		this.paymentsData = JSON.parse(JSON.stringify(paymentData.data));
		this.graphTotal.payments = JSON.parse(JSON.stringify(paymentData.total));
	}

	paymentsDueGraphInfoSuccessCallback(paymentDueData): void {
		this.paymentsDueData = JSON.parse(JSON.stringify(paymentDueData.data));
		this.graphTotal.paymentsDue = JSON.parse(
			JSON.stringify(paymentDueData.total)
		);
	}

	revenueLastYearGraphInfoSuccessCallback(revenueData): void {
		this.revenueData.lastYear = JSON.parse(JSON.stringify(revenueData.data));
	}

	revenueThisYearGraphInfoSuccessCallback(revenueData): void {
		this.revenueData.thisYear = JSON.parse(JSON.stringify(revenueData.data));
	}

	revenueNextYearGraphInfoSuccessCallback(revenueData): void {
		this.revenueData.nextYear = JSON.parse(JSON.stringify(revenueData.data));
	}

	/**
	 * @method getGraphsObservablesAndSuccessCallbacks
	 * @desc gets an array of tabs and depending on it dynamically populates
	 * two arrays with needed observables and success callbacks lists.
	 * @param {string[]} tabs An array of tabs we need fetch new data for
	 * @returns {[Observable<any>[], Function[]]} [observables, callbacks]
	 * an array of two arrays with generated observables and success callbacks
	 * */
	private getGraphsObservablesAndSuccessCallbacks(
		tabs: string[]
	): [Observable<any>[], Function[]] {
		const observables: Observable<any>[] = [];
		const callbacks: Function[] = [];

		tabs.forEach((tab): void => {
			switch (tab) {
				case 'leads':
					observables.push(
						this.graphsDataService.getLeadsGraphInfo(this.selectedFilters)
					);
					callbacks.push(this.leadsGraphInfoSuccessCallback.bind(this));
					break;
				case 'shoots':
					observables.push(
						this.graphsDataService.getShootsGraphInfo(this.selectedFilters)
					);
					callbacks.push(this.shootsGraphInfoSuccessCallback.bind(this));
					break;
				case 'payments':
					observables.push(
						this.graphsDataService.getPaymentsGraphInfo(this.selectedFilters)
					);
					callbacks.push(this.paymentsGraphInfoSuccessCallback.bind(this));

					observables.push(
						this.graphsDataService.getPaymentsDueGraphInfo(
							this.selectedFilters,
							'unpaid'
						)
					);
					callbacks.push(this.paymentsDueGraphInfoSuccessCallback.bind(this));

					const revenueComparisonRanges: RevenueComparisonRangesObject = this.setDateRangesForRevenueComparison();
					observables.push(
						this.graphsDataService.getPaymentsDueGraphInfo(
							revenueComparisonRanges.lastYear
						)
					);
					callbacks.push(
						this.revenueLastYearGraphInfoSuccessCallback.bind(this)
					);

					observables.push(
						this.graphsDataService.getPaymentsDueGraphInfo(
							revenueComparisonRanges.thisYear
						)
					);
					callbacks.push(
						this.revenueThisYearGraphInfoSuccessCallback.bind(this)
					);

					observables.push(
						this.graphsDataService.getPaymentsDueGraphInfo(
							revenueComparisonRanges.nextYear
						)
					);
					callbacks.push(
						this.revenueNextYearGraphInfoSuccessCallback.bind(this)
					);

					break;
				default:
					break;
			}
		});

		return [observables, callbacks];
	}

	private paintGraphsOnTab(tab: string): void {
		switch (tab) {
			case 'lead_menu':
				this.graphsService.createLeadOrShootData(this.leadsData, true);
				break;
			case 'shoot_menu':
				this.graphsService.createLeadOrShootData(this.shootsData, false);
				break;
			case 'payment_menu':
				this.graphsService.createPaymentsData(
					this.paymentsData,
					this.paymentsDueData,
					this.currency
				);
				break;
			case 'revenue_menu':
				this.graphsService.createRevenueData(
					this.revenueData.lastYear,
					this.revenueData.thisYear,
					this.revenueData.nextYear,
					this.currency
				);
				break;
			default:
				break;
		}
	}

	// LOCAL STORAGE

	private getActiveTabFromLocalStorage(): void {
		if (!localStorage.getItem(`dashboardActiveTab${this.companyId}`)) {
			localStorage.setItem(`dashboardActiveTab${this.companyId}`, 'lead_menu');
			this.activeTab = 'lead_menu';
		} else {
			this.activeTab = localStorage.getItem(
				`dashboardActiveTab${this.companyId}`
			);
		}
	}

	private getDateRangeFromLocalStorage(): void {
		if (localStorage.getItem(`presetDashboardDateRange${this.companyId}`)) {
			this.dateRangePreset = localStorage.getItem(
				`presetDashboardDateRange${this.companyId}`
			);
			this.setDateRangePreset(this.dateRangePreset);
		} else if (
			localStorage.getItem(`dashboardFromDate${this.companyId}`) &&
			localStorage.getItem(`dashboardToDate${this.companyId}`)
		) {
			this.fromDate = localStorage.getItem(
				`dashboardFromDate${this.companyId}`
			);
			this.toDate = localStorage.getItem(`dashboardToDate${this.companyId}`);
		} else {
			this.setRangeToLastDays(7);
			this.dateRangePreset = '7';
			localStorage.setItem(`presetDashboardDateRange${this.companyId}`, '7');
		}
	}

	private getActiveJobTypeFromLocalStorage(): void {
		this.selectedJobType =
			parseInt(localStorage.getItem(`dashboardJobType${this.companyId}`)) || -1;
	}

	// HELPERS

	private getCurrencySymbol(): void {
		this.accountService.userDataObservable
			.takeUntil(this.unsubscribe)
			.subscribe((data): void => {
				if (data) {
					this.currency = data.currency.symbol;
				}
			});
	}

	private getCompanyId(): void {
		this.accountService.companyIdObservable
			.takeUntil(this.unsubscribe)
			.subscribe(data => {
				this.companyId = data;
				this.getActiveTabFromLocalStorage();
				this.getDateRangeFromLocalStorage();
				this.getActiveJobTypeFromLocalStorage();
			});
	}

	private setExtremeDates(): void {
		const tenYearsForward: Date = new Date();
		const tenYearsBackward: Date = new Date();

		this.minDate = moment(
			tenYearsBackward.setFullYear(tenYearsBackward.getFullYear() - 10)
		);
		this.maxDate = moment(
			tenYearsForward.setFullYear(tenYearsForward.getFullYear() + 10)
		);
	}

	private setDateRangesForRevenueComparison(): RevenueComparisonRangesObject {
		const thisYear = new Date().getFullYear();

		return {
			thisYear: {
				fromDate: this.timeService.getDateString(new Date(thisYear, 0, 1)),
				toDate: this.timeService.getDateString(new Date(thisYear, 11, 31)),
				jobTypeId: this.selectedFilters.jobTypeId
			},
			lastYear: {
				fromDate: this.timeService.getDateString(new Date(thisYear - 1, 0, 1)),
				toDate: this.timeService.getDateString(new Date(thisYear - 1, 11, 31)),
				jobTypeId: this.selectedFilters.jobTypeId
			},
			nextYear: {
				fromDate: this.timeService.getDateString(new Date(thisYear + 1, 0, 1)),
				toDate: this.timeService.getDateString(new Date(thisYear + 1, 11, 31)),
				jobTypeId: this.selectedFilters.jobTypeId
			}
		};
	}

	private configureCommunicationAdapter(): void {
		window.snCommunicationAdapter.registerMethod(
			'updateGraphsModel',
			this.applyFiltersToGraphsOn.bind(this)
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=nfw9yna8xro!./web/modern/components/dashboard/graphs-block/graphs-block.ts